import "./JobsCard.css";

import { formatDateAgo } from "@helper/useDateFormat";
import { useTMCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useTMCustomEventAnalyticsCall";
import { JobsDetailsItem } from "@typings/Storage.d";
import { useEffect, useRef, useState } from "react";
import sanitizeHtml from "sanitize-html";
import { twMerge } from "tailwind-merge";

export default function JobsCard({
  job_title,
  job_description,
  job_url,
  job_salary,
  job_company,
  job_source,
  updated_date,
}: JobsDetailsItem) {
  const TmCustomGaEvent = useTMCustomEventAnalyticsCall();
  const [isPreferedReadMore, setIsPreferedReadMore] = useState<boolean>(false);
  const togglePreferedReadMoreButton = (url: string) => {
    setIsPreferedReadMore((prev) => !prev);

    TmCustomGaEvent({
      event: "customEvent",
      eventCategory: "Job Search Results",
      eventAction: "View More Click",
      eventLabel: url,
    });
  };

  const getCompanyInitials = (company: string): string => {
    const parts = company.trim().split(" ");
    const first = parts[0]?.charAt(0) || "";
    const second = parts[1]?.charAt(0) || "";
    return (first + second).toUpperCase();
  };

  const cardRef = useRef<HTMLDivElement | null>(null);
  const isFirstRender = useRef(true);
  const prevValue = useRef<boolean>(isPreferedReadMore);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (prevValue.current && !isPreferedReadMore && cardRef.current) {
      const offset = 100;
      const elementTop =
        cardRef.current.getBoundingClientRect().top + window.pageYOffset;
      const scrollTo = elementTop - offset;

      window.scrollTo({
        top: scrollTo,
        behavior: "smooth",
      });
    }
    prevValue.current = isPreferedReadMore;
  }, [isPreferedReadMore]);

  const getJobSourceLabel = (job_source: string): string => {
    switch (job_source) {
      case "stJobs":
        return "ST Jobs";
      case "fastJobs":
        return "FastJobs";
      case "stClassified":
        return "ST Classifieds";
      case "MyCareersFuture":
        return "MyCareersFuture";
      default:
        return "";
    }
  };

  const customGaEvent = (url: string) => {
    TmCustomGaEvent({
      event: "customEvent",
      eventCategory: "Job Search Results",
      eventAction: "Apply Click",
      eventLabel: url,
    });
  };

  // Remove unwanted tags that affect jobs page.
  const jobDescription = sanitizeHtml(job_description);

  // Manage date distance to now.
  const distanceToNowDate = updated_date
    ? formatDateAgo(new Date(Number(updated_date) * 1000).toString())
    : "";

  return (
    <div
      ref={cardRef}
      className="jobs-card block bg-red-400 rounded-sm pb-1 mb-4 px-sm"
    >
      <div className="py-2">
        <a
          href={job_url}
          target="_blank"
          className="text-red-100 font-sans text-base"
          rel="noreferrer"
        >
          சிறப்பு வேலை
        </a>
      </div>
      <div className="ml-2 mb-4">
        <div className="font-semibold font-sans text-md leading-normal">
          {job_title}
        </div>
        <div className="justify-between flex">
          <div className="font-sans font-bold text-sm leading-180 text-black-500 pt-2">
            {job_salary}
          </div>
          <div className="font-sans text-2sm font-normal text-black-600">
            {distanceToNowDate}
          </div>
        </div>
      </div>
      <div
        className={twMerge(
          "mb-4 transition-all duration-500 ease-in-out transform overflow-hidden",
          isPreferedReadMore ? "max-h-[10000px]" : "max-h-[5.75rem]",
        )}
      >
        <div
          className="text-base leading-180 font-sans font-normal"
          dangerouslySetInnerHTML={{ __html: jobDescription }}
        />
      </div>
      <div>
        <div className="inline-flex text-lg text-white-100 bg-gray-2300 justify-center items-center rounded-full w-[49px] h-[44px] font-primary font-semibold">
          {getCompanyInitials(job_company)}
        </div>
        <div className="inline-flex w-full justify-between">
          <div className="font-sans text-base font-normal text-black-600">
            {job_company}
          </div>
          <div className="font-sans text-black-500 text-2sm">
            via{" "}
            <span className="text-black-100 leading-180">
              {getJobSourceLabel(job_source)}
            </span>
          </div>
        </div>
        <div
          className={twMerge(
            "mx-auto py-4 justify-items-center overflow-hidden transition-[max-height,opacity] duration-500 ease-in-out",
            isPreferedReadMore
              ? "max-h-40 opacity-100 scale-y-100"
              : "max-h-0 opacity-0 scale-y-0 !py-0 pointer-events-none",
          )}
        >
          <a
            onClick={() => {
              customGaEvent(job_url);
            }}
            href={job_url}
            target="_blank"
            className="!block !w-fit py-1.5 px-3 !rounded !text-base !font-semibold !bg-pink-700 !text-white-100"
            rel="noreferrer"
          >
            வேலைக்கு விண்ணப்பம் செய்
          </a>
        </div>

        <div className="border-t-solid border-t border-gray-2100">
          <button
            onClick={() => {
              togglePreferedReadMoreButton(job_url);
            }}
            className={twMerge(
              "mx-auto py-2 flex text-red-950 font-primary font-semibold text-base leading-650 items-center after:inline-block after:content-[''] after:ml-1 after:border-solid after:border-r-4 after:border-r-transparent after:border-l-4 after:border-l-transparent",
              isPreferedReadMore
                ? "after:border-t-0 after:border-b-4 after:border-b-red-950"
                : "after:border-b-0 after:border-t-4 after:border-t-red-950",
            )}
          >
            மேலும் பார்க்க
          </button>
        </div>
      </div>
    </div>
  );
}
